import { usePipelineContext } from '@/context/pipelineContext';
import { useTheme } from '@mui/material';
import { DEAL_STATUS_TYPES, RENEWAL_MAX_DAYS } from '@/utils/constants';
import { safelyParseDate } from '@/utils/date';
import { addDays, isAfter } from 'date-fns';
import { useEffect, useMemo } from 'react';

const today = new Date();

export const getRenewalStatuses = ({ deal, transactions }) => {
	const theme = useTheme();

	const renewalDate = useMemo(
		() => (deal.renewalDate ? safelyParseDate(deal.renewalDate) : new Date()),
		[deal.renewalDate],
	);

	const {
		pipelines,
		stages,
		active: activeStages,
		inactive: inactiveStages,
		closed,
		endorsements,
		...pipelineQuery
	} = usePipelineContext();
	const active = activeStages.map((s) =>
		typeof s === 'number' || typeof s === 'string' ? s : s.id,
	);
	const inactive = inactiveStages.map((s) =>
		typeof s === 'number' || typeof s === 'string' ? s : s.id,
	);
	const pipelineId = endorsements.includes(deal?.dealStage)
		? process.env.REACT_APP_PIPELINE_ENDORSEMENTS
		: deal?.pipeline;
	const pipeline = pipelines.find((p) => p?.id === pipelineId);

	const stage = useMemo(
		() => stages.find((s) => s.id === deal?.dealStage) ?? { label: deal.dealStage, order: 99 },
		[stages],
	);

	const isRenewable = useMemo(
		() => transactions.length > 0 && inactive.includes(deal.dealStage),
		[deal.dealStage, inactive, transactions],
	);

	const showInRed = useMemo(
		() => (!deal.renewalDate ? false : isAfter(today, renewalDate)),
		[renewalDate, deal.renewalDate],
	);

	const showInOrange = useMemo(
		() => (!deal.renewalDate ? false : isAfter(today, addDays(renewalDate, -RENEWAL_MAX_DAYS))),
		[renewalDate, deal.renewalDate],
	);

	const status = useMemo(() => {
		if (deal?.status === DEAL_STATUS_TYPES.archived) {
			return DEAL_STATUS_TYPES.archived;
		}

		if (inactive.includes(deal.dealStage)) {
			return 'Completed';
		}

		if (active.includes(deal.dealStage)) {
			return 'In Progress';
		}
		//commented out was for renewals faster response
		return transactions.length > 0
			? (deal.status ?? DEAL_STATUS_TYPES.notStarted)
			: 'Not Renewable';
		// return deal.hasPolicyTransaction && deal?.isRenewable != false
		// 	? deal.status ?? DEAL_STATUS_TYPES.notStarted
		// 	: 'Not Renewable';
	}, [active, deal.dealStage, inactive, transactions, deal.status]);

	const statusColour = useMemo(() => {
		if (status == 'In Progress') {
			return theme.palette.primary.main;
		}
		if (showInRed && status == DEAL_STATUS_TYPES.notStarted) {
			return '#FF604B';
		}
		if (status == DEAL_STATUS_TYPES.notStarted) {
			return '#F28505';
		}
		return 'rgba(0,0,0,0.5)';
	}, [status, showInRed, theme]);

	return {
		statusColour,
		showInRed,
		showInOrange,
		isRenewable,
		renewalDate,
		status,
		stage,
		pipeline,
		// stages
	};
};
